import React, { lazy, useContext } from 'react'
import { Route } from 'react-router-dom'
import PermissionDenied from 'components/common/permissions/PermissionDenied'
import AuthContext from 'contexts/AuthContext'
import { FacilityDetailsProvider } from 'contexts/FacilityDetailsContext'
import { FacilityConfigProvider } from 'contexts/FacilityConfigContext'
import { useAdminPermission } from 'hooks/useAdminPermission'
import { useParams } from 'react-router-dom'

const PermissionViewPage = lazy(() => import('pages/AVPPermission/PermissionViewPage'))
const PermissionEditPage = lazy(() => import('pages/AVPPermission/PermissionEditPage'))
const PermissionLoadsheetPage = lazy(() => import('pages/AVPPermission/PermissionLoadsheetPage'))
const BulkRoleAssignmentPage = lazy(() => import('pages/AVPPermission/BulkRoleAssignmentPage'))
const RoleListPage = lazy(() => import('pages/AVPPermission/RoleListPage'))
const RoleFormPage = lazy(() => import('pages/AVPPermission/RoleFormPage'))

type AVPManagementPageProps = {
  children: React.ReactElement
  staffOnly?: boolean
  requiredPermission?:
    | 'managePermissions'
    | 'createUpdatePermissions'
    | 'manageRoles'
    | 'createUpdateRoles'
}

const AVPManagementPage = ({
  children,
  staffOnly = false,
  requiredPermission = 'managePermissions',
}: AVPManagementPageProps) => {
  const { isStaff } = useContext(AuthContext)
  const { project_id } = useParams()
  const {
    canManagePermissions,
    canCreateUpdatePermissions,
    canManageRoles,
    canCreateUpdateRoles,
    isLoading,
  } = useAdminPermission(project_id)

  const hasRequiredPermission = {
    managePermissions: canManagePermissions,
    createUpdatePermissions: canCreateUpdatePermissions,
    manageRoles: canManageRoles,
    createUpdateRoles: canCreateUpdateRoles,
  }[requiredPermission]

  if (!isStaff && isLoading) {
    return null
  }

  if (staffOnly && !isStaff) {
    return <PermissionDenied />
  }

  if (!isStaff && !hasRequiredPermission) {
    return <PermissionDenied />
  }

  return children
}

const BarrierRoutes = (
  <>
    <Route
      path="/admin/:project_id/permission"
      element={
        <AVPManagementPage>
          <FacilityConfigProvider slug="project_feature_config">
            <FacilityDetailsProvider>
              <PermissionViewPage />
            </FacilityDetailsProvider>
          </FacilityConfigProvider>
        </AVPManagementPage>
      }
    />
    <Route
      path="/admin/:project_id/permission/edit"
      element={
        <AVPManagementPage requiredPermission="createUpdatePermissions">
          <FacilityConfigProvider slug="project_feature_config">
            <FacilityDetailsProvider>
              <PermissionEditPage />
            </FacilityDetailsProvider>
          </FacilityConfigProvider>
        </AVPManagementPage>
      }
    />
    <Route
      path="/admin/:project_id/permission/loadsheet"
      element={
        <AVPManagementPage staffOnly>
          <FacilityConfigProvider slug="project_feature_config">
            <FacilityDetailsProvider>
              <PermissionLoadsheetPage />
            </FacilityDetailsProvider>
          </FacilityConfigProvider>
        </AVPManagementPage>
      }
    />
    <Route
      path="/admin/:project_id/permission/bulk-assign"
      element={
        <AVPManagementPage requiredPermission="createUpdatePermissions">
          <FacilityConfigProvider slug="project_feature_config">
            <FacilityDetailsProvider>
              <BulkRoleAssignmentPage />
            </FacilityDetailsProvider>
          </FacilityConfigProvider>
        </AVPManagementPage>
      }
    />
    <Route
      path="/admin/:project_id/roles"
      element={
        <AVPManagementPage requiredPermission="manageRoles">
          <FacilityConfigProvider slug="project_feature_config">
            <FacilityDetailsProvider>
              <RoleListPage />
            </FacilityDetailsProvider>
          </FacilityConfigProvider>
        </AVPManagementPage>
      }
    />
    <Route
      path="/admin/:project_id/roles/add"
      element={
        <AVPManagementPage requiredPermission="createUpdateRoles">
          <FacilityConfigProvider slug="project_feature_config">
            <FacilityDetailsProvider>
              <RoleFormPage />
            </FacilityDetailsProvider>
          </FacilityConfigProvider>
        </AVPManagementPage>
      }
    />
    <Route
      path="/admin/:project_id/roles/:role_id/view"
      element={
        <AVPManagementPage requiredPermission="manageRoles">
          <FacilityConfigProvider slug="project_feature_config">
            <FacilityDetailsProvider>
              <RoleFormPage />
            </FacilityDetailsProvider>
          </FacilityConfigProvider>
        </AVPManagementPage>
      }
    />
    <Route
      path="/admin/:project_id/roles/:role_id/edit"
      element={
        <AVPManagementPage requiredPermission="createUpdateRoles">
          <FacilityConfigProvider slug="project_feature_config">
            <FacilityDetailsProvider>
              <RoleFormPage />
            </FacilityDetailsProvider>
          </FacilityConfigProvider>
        </AVPManagementPage>
      }
    />
  </>
)

export default BarrierRoutes
